import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const EASE_REFINED = [0.22, 1, 0.36, 1];

const integrations = [
  { name: 'BambooHR',           src: '/integrations/bamboohr-white.png', width: 108 },
  { name: 'ADP',                src: '/integrations/adp-white.png',      width: 68  },
  { name: 'Workday',            src: '/integrations/workday-white.png',  width: 106 },
  { name: 'Gusto',              src: '/integrations/gusto-white.png',    width: 76  },
  { name: 'Oracle',             src: '/integrations/oracle-white.png',   width: 92  },
  { name: 'Namely',             src: '/integrations/namely-white.png',   width: 86  },
  { name: 'HiBob',              src: '/integrations/hibob-white.png',    width: 86  },
  { name: 'Rippling',           src: '/integrations/rippling-white.png', width: 110 },
  { name: 'Deel',               src: '/integrations/deel-white.png',     width: 72  },
  { name: 'Personio',           src: '/integrations/personio-white.png', width: 96  },
  { name: 'Paychex',            src: '/integrations/paychex-white.png',  width: 100 },
  { name: 'Paycor',             src: '/integrations/paycor-white.png',   width: 84  },
  { name: 'Keka',               src: '/integrations/keka-white.png',     width: 72  },
  { name: 'SAP SuccessFactors', src: '/integrations/sap-white.png',      width: 112 },
];

// Triple the logos so we can animate by 1/3 and loop seamlessly (no visible reset)
const tripled = [...integrations, ...integrations, ...integrations];

export default function IntegrationsStrip({ showTopBorder = true, flowThrough = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const labelBg = flowThrough ? 'var(--bg-base)' : 'var(--integrations-strip-bg)';
  const edgeFade = flowThrough ? 'var(--bg-base)' : 'var(--integrations-strip-fade)';

  return (
    <section
      ref={ref}
      className={`integrations-strip${flowThrough ? ' integrations-strip--flow-through' : ''}`}
      style={{
        borderTop: showTopBorder ? '1px solid var(--integrations-strip-border)' : 'none',
        borderBottom: flowThrough ? 'none' : '1px solid var(--integrations-strip-border)',
        padding: flowThrough ? '2rem 0 0' : '2.75rem 0',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {!flowThrough && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse at 50% 50%, var(--integrations-strip-glow) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}/>
      )}

      <div className="max-w-7xl mx-auto px-8 lg:px-12" style={{ position: 'relative', zIndex: 1 }}>
        {/* Label row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, ease: EASE_REFINED }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
            marginBottom: '1.75rem',
          }}
        >
          <span style={{
            fontFamily: 'Inter, DM Sans, sans-serif',
            fontWeight: 500,
            fontSize: '0.5625rem',
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            color: 'var(--integrations-strip-label)',
            whiteSpace: 'nowrap',
            background: labelBg,
            border: '1px solid var(--integrations-strip-border)',
            borderRadius: 6,
            padding: '0.625rem 0.875rem',
          }}>
            Connects with your HR stack
          </span>
        </motion.div>
      </div>

      {/* Scrolling marquee — seamless infinite loop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.7, ease: EASE_REFINED, delay: 0.2 }}
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: flowThrough ? 'transparent' : 'var(--integrations-strip-bg)',
        }}
      >
        {flowThrough && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 0,
              background:
                'radial-gradient(ellipse 92% 88% at 50% 50%, var(--bg-base) 0%, color-mix(in srgb, var(--bg-base) 72%, transparent) 52%, transparent 76%)',
            }}
          />
        )}
        {/* Left fade mask */}
        <div style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 120,
          background: `linear-gradient(90deg, ${edgeFade}, transparent)`,
          zIndex: 2,
          pointerEvents: 'none',
        }}/>
        {/* Right fade mask */}
        <div style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 120,
          background: `linear-gradient(-90deg, ${edgeFade}, transparent)`,
          zIndex: 2,
          pointerEvents: 'none',
        }}/>

        {/* Track: 3 copies, animate -33.333% so loop is visually identical */}
        <div
          className="integrations-marquee-track"
          style={{
            display: 'flex',
            gap: '4.5rem',
            alignItems: 'center',
            width: 'max-content',
            willChange: 'transform',
            padding: '1.125rem 0',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {tripled.map((logo, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.88,
                transition: 'opacity 0.4s ease',
                flexShrink: 0,
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0.88'}
            >
              <img
                src={logo.src}
                alt={logo.name}
                style={{
                  width: logo.width,
                  height: 'auto',
                  maxHeight: 26,
                  objectFit: 'contain',
                  display: 'block',
                }}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </motion.div>

      {flowThrough && (
        <div
          aria-hidden="true"
          style={{
            height: '2.75rem',
            pointerEvents: 'none',
          }}
        />
      )}

      <style>{`
        @keyframes marquee-scroll {
          0%   { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-33.333%, 0, 0); }
        }
        .integrations-marquee-track {
          animation: marquee-scroll 48s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .integrations-marquee-track { animation: none !important; }
        }
      `}</style>
    </section>
  );
}
